import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    Plus,
    Edit,
    Trash2,
    LogOut,
    Save,
    X,
    Eye,
    EyeOff,
    ArrowLeft,
    Filter,
} from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/common/SEO";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

const EMPTY = {
    title: "",
    text: "",
    section: "apertura",
    published: true,
    order: null,
};

export default function AdminAntipoesia() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [sections, setSections] = useState([]);
    const [fragments, setFragments] = useState([]);
    const [filter, setFilter] = useState(null);
    const [editing, setEditing] = useState(null); // null | "new" | fragment
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(false);

    const reload = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/antipoesia");
            setFragments(data);
        } catch (e) {
            toast.error("No se pudo cargar el archivo.");
        }
    }, []);

    useEffect(() => {
        api.get("/antipoesia/sections")
            .then(({ data }) => setSections(data))
            .catch(() => setSections([]));
        reload();
    }, [reload]);

    const filtered = useMemo(() => {
        if (!filter) return fragments;
        return fragments.filter((f) => f.section === filter);
    }, [fragments, filter]);

    const countsBySection = useMemo(() => {
        const map = {};
        for (const f of fragments) {
            map[f.section] = (map[f.section] || 0) + 1;
        }
        return map;
    }, [fragments]);

    function getSectionMeta(id) {
        return sections.find((s) => s.id === id) || { number: "?", title: id };
    }

    function startNew() {
        setForm({ ...EMPTY, section: filter || "apertura" });
        setEditing("new");
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    function startEdit(frag) {
        setForm({
            title: frag.title,
            text: frag.text,
            section: frag.section,
            published: frag.published,
            order: frag.order,
        });
        setEditing(frag);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    function cancel() {
        setEditing(null);
        setForm(EMPTY);
    }

    async function save(e) {
        e.preventDefault();
        setLoading(true);
        const payload = { ...form };
        if (payload.order === null || payload.order === "") {
            delete payload.order;
        } else {
            payload.order = Number(payload.order);
        }
        try {
            if (editing === "new") {
                await api.post("/admin/antipoesia", payload);
                toast.success("Antipoesía creada.");
            } else {
                await api.put(`/admin/antipoesia/${editing.id}`, payload);
                toast.success("Antipoesía actualizada.");
            }
            await reload();
            cancel();
        } catch (e) {
            toast.error(
                formatApiError(e.response?.data?.detail) ||
                    "Error al guardar.",
            );
        } finally {
            setLoading(false);
        }
    }

    async function remove(frag) {
        if (
            !confirm(
                `¿Eliminar "${frag.title}"? Esta antipoesía dejará de aparecer en el sitio.`,
            )
        )
            return;
        try {
            await api.delete(`/admin/antipoesia/${frag.id}`);
            toast.success("Antipoesía eliminada.");
            await reload();
        } catch (e) {
            toast.error("Error al eliminar.");
        }
    }

    async function togglePublished(frag) {
        try {
            await api.put(`/admin/antipoesia/${frag.id}`, {
                published: !frag.published,
            });
            toast.success(
                frag.published ? "Despublicada." : "Publicada.",
            );
            await reload();
        } catch (e) {
            toast.error("Error al actualizar.");
        }
    }

    async function handleLogout() {
        await logout();
        navigate("/login");
    }

    return (
        <>
            <SEO
                title="Admin · Antipoesía"
                description="Panel administrativo de antipoesías"
                path="/admin/antipoesia"
            />
            <section className="py-12 md:py-16" data-testid="admin-antipoesia">
                <div className="container-x">
                    {/* Header */}
                    <div className="flex flex-wrap justify-between items-start gap-4 mb-12 pb-8 border-b border-[var(--line)]">
                        <div>
                            <Link
                                to="/admin"
                                className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] hover:text-[var(--danger)] mb-4"
                                data-testid="admin-back-to-hub"
                            >
                                <ArrowLeft className="w-3 h-3" />
                                Volver al panel
                            </Link>
                            <p className="eyebrow mb-3">Departamento · Antipoesía</p>
                            <h1 className="font-display text-4xl md:text-6xl text-[var(--paper-2)] leading-[0.95]">
                                Antipoesía · Administración
                            </h1>
                            <p className="font-mono text-xs text-[var(--ink-muted)] mt-2">
                                Sesión: {user?.email} · {fragments.length}{" "}
                                fragmentos totales
                            </p>
                        </div>
                        <div className="flex gap-3 flex-wrap">
                            <button
                                onClick={startNew}
                                className="btn-brutal btn-brutal-danger"
                                data-testid="admin-new-fragment"
                            >
                                <Plus className="w-4 h-4" />
                                Nueva antipoesía
                            </button>
                            <button
                                onClick={handleLogout}
                                className="btn-brutal btn-brutal-ghost"
                                data-testid="admin-logout"
                            >
                                <LogOut className="w-4 h-4" />
                                Salir
                            </button>
                        </div>
                    </div>

                    {/* Form */}
                    {editing && (
                        <form
                            onSubmit={save}
                            className="dossier mb-12"
                            data-testid="admin-form"
                        >
                            <div className="dossier-header">
                                <span>
                                    {editing === "new"
                                        ? "Nueva antipoesía"
                                        : `Editando: ${editing.title}`}
                                </span>
                                <button
                                    type="button"
                                    onClick={cancel}
                                    className="text-[var(--ink-dim)] hover:text-[var(--danger)]"
                                    data-testid="admin-form-close"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="dossier-content space-y-6">
                                <div className="grid sm:grid-cols-3 gap-6">
                                    <div className="sm:col-span-2">
                                        <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] block mb-2">
                                            Título
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={form.title}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    title: e.target.value,
                                                })
                                            }
                                            className="input-dossier"
                                            data-testid="admin-form-title"
                                            placeholder="Ej: Vacío"
                                        />
                                    </div>
                                    <div>
                                        <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] block mb-2">
                                            Orden (opcional)
                                        </label>
                                        <input
                                            type="number"
                                            value={form.order ?? ""}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    order:
                                                        e.target.value === ""
                                                            ? null
                                                            : Number(
                                                                  e.target.value,
                                                              ),
                                                })
                                            }
                                            className="input-dossier"
                                            data-testid="admin-form-order"
                                            placeholder="auto"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] block mb-2">
                                        Sección
                                    </label>
                                    <select
                                        value={form.section}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                section: e.target.value,
                                            })
                                        }
                                        className="input-dossier"
                                        data-testid="admin-form-section"
                                    >
                                        {sections.map((s) => (
                                            <option
                                                key={s.id}
                                                value={s.id}
                                                style={{ background: "#111" }}
                                            >
                                                {s.number} — {s.title}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)] block mb-2">
                                        Texto (saltos de línea respetados)
                                    </label>
                                    <textarea
                                        required
                                        rows={10}
                                        value={form.text}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                text: e.target.value,
                                            })
                                        }
                                        className="input-dossier resize-y font-display italic"
                                        data-testid="admin-form-text"
                                        placeholder="Escribe el verso aquí, con sus saltos de línea originales…"
                                    />
                                </div>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        id="published"
                                        checked={form.published}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                published: e.target.checked,
                                            })
                                        }
                                        className="w-4 h-4 accent-[var(--danger)]"
                                        data-testid="admin-form-published"
                                    />
                                    <label
                                        htmlFor="published"
                                        className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--paper-2)] cursor-pointer"
                                    >
                                        Publicada
                                    </label>
                                </div>
                                <div className="flex gap-3 pt-4 border-t border-dashed border-[var(--ink-dim)]">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="btn-brutal btn-brutal-danger disabled:opacity-60"
                                        data-testid="admin-form-save"
                                    >
                                        <Save className="w-4 h-4" />
                                        {loading ? "Guardando…" : "Guardar"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={cancel}
                                        className="btn-brutal btn-brutal-ghost"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}

                    {/* Filter chips */}
                    <div className="mb-8">
                        <div className="flex items-center gap-2 mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--danger)]">
                            <Filter className="w-3 h-3" />
                            Filtrar por sección
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setFilter(null)}
                                className={`font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-2 border ${
                                    !filter
                                        ? "bg-[var(--paper-2)] text-black border-[var(--paper-2)]"
                                        : "border-[var(--line)] text-[var(--paper)] hover:border-[var(--danger)]"
                                }`}
                                data-testid="filter-all"
                            >
                                Todas · {fragments.length}
                            </button>
                            {sections.map((s) => {
                                const count = countsBySection[s.id] || 0;
                                return (
                                    <button
                                        key={s.id}
                                        onClick={() => setFilter(s.id)}
                                        className={`font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-2 border ${
                                            filter === s.id
                                                ? "bg-[var(--danger)] text-[var(--paper-2)] border-[var(--danger)]"
                                                : "border-[var(--line)] text-[var(--paper)] hover:border-[var(--danger)]"
                                        }`}
                                        data-testid={`filter-${s.id}`}
                                    >
                                        <span className="text-[var(--ink-dim)] mr-1">
                                            {s.number}/
                                        </span>
                                        {s.title}
                                        <span className="ml-1 text-[var(--ink-dim)]">
                                            · {count}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Fragments list */}
                    <div
                        className="space-y-3"
                        data-testid="admin-fragments-list"
                    >
                        <p className="eyebrow mb-4">
                            {filtered.length}{" "}
                            {filtered.length === 1
                                ? "fragmento"
                                : "fragmentos"}{" "}
                            {filter ? `· filtro activo` : "· total"}
                        </p>
                        {filtered.map((frag) => {
                            const meta = getSectionMeta(frag.section);
                            return (
                                <article
                                    key={frag.id}
                                    className="border border-[var(--line)] bg-[#1a1a1a] p-5 hover:border-[var(--danger)] transition-colors"
                                    data-testid={`admin-frag-${frag.id}`}
                                >
                                    <div className="flex flex-wrap items-start gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-3 mb-1 flex-wrap">
                                                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--danger)]">
                                                    {meta.number}/ {meta.title}
                                                </span>
                                                {!frag.published && (
                                                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--warning)]">
                                                        Borrador
                                                    </span>
                                                )}
                                                <span className="font-mono text-[10px] text-[var(--ink-dim)]">
                                                    Orden: {frag.order}
                                                </span>
                                            </div>
                                            <h3 className="font-display text-xl text-[var(--paper-2)] mb-1">
                                                — {frag.title} —
                                            </h3>
                                            <p className="font-mono text-xs text-[var(--ink-muted)] line-clamp-2 italic">
                                                {frag.text}
                                            </p>
                                        </div>
                                        <div className="flex gap-2 flex-shrink-0">
                                            <button
                                                onClick={() =>
                                                    togglePublished(frag)
                                                }
                                                className="p-2 border border-[var(--line)] text-[var(--paper-2)] hover:border-[var(--warning)] hover:text-[var(--warning)]"
                                                title={
                                                    frag.published
                                                        ? "Despublicar"
                                                        : "Publicar"
                                                }
                                                data-testid={`admin-toggle-${frag.id}`}
                                            >
                                                {frag.published ? (
                                                    <Eye className="w-4 h-4" />
                                                ) : (
                                                    <EyeOff className="w-4 h-4" />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => startEdit(frag)}
                                                className="p-2 border border-[var(--line)] text-[var(--paper-2)] hover:border-[var(--danger)] hover:text-[var(--danger)]"
                                                data-testid={`admin-edit-${frag.id}`}
                                            >
                                                <Edit className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => remove(frag)}
                                                className="p-2 border border-[var(--danger)] text-[var(--danger)] hover:bg-[var(--danger)] hover:text-[var(--paper-2)]"
                                                data-testid={`admin-delete-${frag.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                        {filtered.length === 0 && (
                            <p className="font-mono text-sm text-[var(--ink-muted)] py-10 text-center">
                                Sin fragmentos en esta sección.
                            </p>
                        )}
                    </div>
                </div>
            </section>
        </>
    );
}
